import { useMemo } from "react";
import { Bell, ChevronRight, X } from "lucide-react";
import useDismissible from "@/lib/useDismissible";

/**
 * PendingBookingsBanner (iter181 – 02/2026)
 * ------------------------------------------------------------------
 * Banner sempre visibile in cima al tab "Prenotazioni" quando ci sono
 * richieste ancora in attesa di conferma.
 *
 * Nasconde se stesso automaticamente quando:
 *  - non ci sono pending; oppure
 *  - il filtro attivo è già "pending" (l'host le sta già vedendo).
 *
 * Include:
 *  - Contatore numerico grande + label ("prenotazione da confermare" / "prenotazioni da confermare")
 *  - CTA "Vai alle pendenti" che azzera i filtri di data e imposta status=pending
 *  - Elenco dei primi 3 nomi ospite + date, così l'host sa già chi è
 *    senza dover cliccare (evita "devo cercare la data giusta")
 *  - Animazione pulsante rossa sul badge per attirare l'occhio
 */
export default function PendingBookingsBanner({ bookings, currentFilterStatus, onGoToPending }) {
  // iter190 — banner chiudibile per 7 giorni; se ci sono pending nuove
  // (count aumenta) il dismiss viene ignorato così l'host non le perde.
  const [dismissed, dismiss] = useDismissible("pending-bookings", 7);
  const pending = useMemo(() => {
    if (!Array.isArray(bookings)) return [];
    // Tutte le pending, ordinate per data check-in crescente. Includiamo
    // anche quelle con check-in passato: se l'host non le ha confermate
    // finora, o le conferma o le cancella — non le sotterriamo mai.
    return bookings
      .filter((b) => b.status === "pending")
      .sort((a, b) => (a.check_in || "").localeCompare(b.check_in || ""));
  }, [bookings]);

  if (pending.length === 0) return null;
  if (currentFilterStatus === "pending") return null;
  if (dismissed) return null;

  const preview = pending.slice(0, 3);
  const extra = pending.length - preview.length;

  const fmtDate = (iso) => {
    if (!iso) return "";
    try {
      const d = new Date(iso);
      return d.toLocaleDateString("it-IT", { day: "2-digit", month: "short" });
    } catch {
      return iso;
    }
  };

  const guestName = (b) => {
    const g = b.guest || {};
    const fn = (g.first_name || "").trim();
    const ln = (g.last_name || "").trim();
    return `${fn} ${ln}`.trim() || "(senza nome)";
  };

  return (
    <div
      className="relative overflow-hidden rounded-xl border-2 border-amber-400 bg-gradient-to-r from-amber-50 to-orange-50 p-4 mb-3 shadow-sm"
      data-testid="pending-bookings-banner"
    >
      {/* Pulsing red dot for attention */}
      <span className="absolute top-3 right-11 flex h-3 w-3">
        <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-red-400 opacity-75"></span>
        <span className="relative inline-flex rounded-full h-3 w-3 bg-red-500"></span>
      </span>
      {/* iter190 — bottone X per chiudere il banner per 7 giorni */}
      <button
        onClick={dismiss}
        className="absolute top-1.5 right-1.5 p-1 rounded hover:bg-amber-200/60 text-amber-800/80 hover:text-amber-900 transition-colors"
        aria-label="Chiudi per 7 giorni"
        title="Nascondi per 7 giorni"
        data-testid="pending-banner-dismiss"
      >
        <X className="w-4 h-4" />
      </button>

      <div className="flex items-start gap-3">
        <div className="shrink-0 h-11 w-11 rounded-full bg-amber-500 text-white flex items-center justify-center shadow-md">
          <Bell className="w-6 h-6" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-baseline gap-2 flex-wrap">
            <span
              className="text-2xl font-bold text-amber-900 tabular-nums"
              data-testid="pending-count"
            >
              {pending.length}
            </span>
            <span className="text-sm font-semibold text-amber-900">
              {pending.length === 1
                ? "prenotazione da confermare"
                : "prenotazioni da confermare"}
            </span>
          </div>
          <ul className="mt-1.5 space-y-0.5 text-xs text-amber-800">
            {preview.map((b) => (
              <li key={b.code} className="truncate">
                <span className="font-medium">{guestName(b)}</span>
                {b.check_in && (
                  <span className="text-amber-700"> · {fmtDate(b.check_in)}</span>
                )}
                {b.room_name && (
                  <span className="text-amber-600 italic"> · {typeof b.room_name === "string" ? b.room_name : (b.room_name.it || "")}</span>
                )}
              </li>
            ))}
            {extra > 0 && (
              <li className="text-amber-600 italic">…e altre {extra}</li>
            )}
          </ul>
        </div>
        <button
          type="button"
          onClick={onGoToPending}
          className="shrink-0 self-center inline-flex items-center gap-1 bg-amber-600 hover:bg-amber-700 text-white text-sm font-semibold px-3 py-2 rounded-lg shadow-sm transition-colors"
          data-testid="pending-goto-btn"
        >
          Vai alle pendenti
          <ChevronRight className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
}
